import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DollarSign, Calculator, Briefcase, Users, TrendingUp, TrendingDown } from 'lucide-react';
import { SpendKPIs } from '@/hooks/useSpendAnalytics';

interface SpendKPICardsProps {
  data: SpendKPIs | undefined;
  isLoading: boolean;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatPercentage(current: number, previous: number): { value: string; isPositive: boolean } | null {
  if (previous === 0) return null;
  const change = ((current - previous) / previous) * 100;
  return {
    value: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
    isPositive: change >= 0,
  };
}

interface TrendIndicatorProps {
  current: number;
  previous: number;
  invertColors?: boolean;
}

function TrendIndicator({ current, previous, invertColors = false }: TrendIndicatorProps) {
  const trend = formatPercentage(current, previous);
  
  if (!trend) {
    return <span className="text-xs text-muted-foreground">N/A vs prev period</span>;
  }

  const isGood = invertColors ? !trend.isPositive : trend.isPositive;
  const Icon = trend.isPositive ? TrendingUp : TrendingDown;

  return (
    <span className={`flex items-center gap-1 text-xs ${isGood ? 'text-green-600' : 'text-red-600'}`}>
      <Icon className="h-3 w-3" />
      {trend.value} vs prev period
    </span>
  );
}

interface KPICardProps {
  title: string;
  icon: React.ReactNode;
  value: string;
  subtitle: string;
  trend?: React.ReactNode;
  isLoading: boolean;
}

function KPICard({ title, icon, value, subtitle, trend, isLoading }: KPICardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-8 rounded-full" />
          </div>
          <Skeleton className="h-8 w-32 mb-2" />
          <Skeleton className="h-4 w-28 mb-2" />
          <Skeleton className="h-3 w-24" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-muted-foreground">{title}</span>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
            {icon}
          </div>
        </div>
        <div className="text-2xl font-bold tracking-tight mb-1">{value}</div>
        <div className="text-sm text-muted-foreground mb-2">{subtitle}</div>
        {trend}
      </CardContent>
    </Card>
  );
}

export function SpendKPICards({ data, isLoading }: SpendKPICardsProps) {
  const activeBreakdown = data?.activeJobsByStatus
    ? `${data.activeJobsByStatus.bidding} in bidding, ${data.activeJobsByStatus.production} in production`
    : '';

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <KPICard
        title="Total Spend"
        icon={<DollarSign className="h-4 w-4 text-primary" />}
        value={data ? formatCurrency(data.totalSpendCents) : '$0'}
        subtitle={data ? `across ${data.jobCount} jobs` : 'across 0 jobs'}
        trend={
          data && (
            <TrendIndicator
              current={data.totalSpendCents}
              previous={data.prevTotalSpendCents}
            />
          )
        }
        isLoading={isLoading}
      />

      <KPICard
        title="Average Job Value"
        icon={<Calculator className="h-4 w-4 text-primary" />}
        value={data ? formatCurrency(data.avgJobValueCents) : '$0'}
        subtitle={data ? `Median: ${formatCurrency(data.medianJobValueCents)}` : 'Median: $0'}
        trend={
          data && (
            <TrendIndicator
              current={data.avgJobValueCents}
              previous={data.prevAvgJobValueCents}
            />
          )
        }
        isLoading={isLoading}
      />

      <KPICard
        title="Active Jobs"
        icon={<Briefcase className="h-4 w-4 text-primary" />}
        value={data ? data.activeJobsCount.toString() : '0'}
        subtitle={activeBreakdown || 'No active jobs'}
        isLoading={isLoading}
      />

      <KPICard
        title="Total Vendors Used"
        icon={<Users className="h-4 w-4 text-primary" />}
        value={data ? data.uniqueVendorCount.toString() : '0'}
        subtitle={data ? `${data.newVendorCount} new vendors this period` : '0 new vendors'}
        trend={
          data && (
            <TrendIndicator
              current={data.uniqueVendorCount}
              previous={data.prevVendorCount}
            />
          )
        }
        isLoading={isLoading}
      />
    </div>
  );
}
